/* eslint-disable class-methods-use-this */
import get from 'lodash/get';
import { SearchTypes } from '@Constants/CommonConstants';
import Breadcrumb from '@Services/Breadcrumb';
import { NamespacedCache } from '@Services/NamespacedCache';
import searchStorage from '@Services/SearchStorage';
import ResultsInfo from '@Services/ResultsInfo';
import { dispatchHideGlobalSpinner, dispatchShowGlobalSpinner } from '@Containers/GlobalSpinner/GlobalSpinnerActions';
import { showDialogZeroResults, showSimilarityNotPossible } from '@Containers/FindPreparations/utils';
import { dispatchOpenFindPreparationsSimilaritySearchDialog } from '@Containers/FindPreparations/SimilatiySearch/dialogActions';
import { generateResultsPath } from '@AppRoutes/utils';
import history from '@AppRoutes/history';
import { trackFindPreparations } from './analytics';
import findPreparationsWSService from './FindPreparationsWSService';

const serviceCacheKey = 'FindPreparationsService';
const cacheSizeLimit = 20;

class FindPreparationsService {
  _cache = null;

  constructor() {
    this._cache = new NamespacedCache(cacheSizeLimit);
  }

  async _findPreparations(substanceId, databaseId) {
    const cacheKey = databaseId + substanceId;
    const cached = this._cache.getByKey([serviceCacheKey, cacheKey]);
    if (cached) {
      return Promise.resolve(cached);
    }

    try {
      const { databases, productsMap } = await findPreparationsWSService.findPreparations(substanceId, databaseId);
      this._cache.setByKey([serviceCacheKey, cacheKey], { databases, productsMap });
      return { databases, productsMap };
    } catch (error) {
      return Logger.reject(error);
    }
  }

  // Similarity search
  _launchSimilarity = (substance, databaseId, context) => {
    // RXFE-9129
    if (!get(substance, 'molImage.structure')) {
      return showSimilarityNotPossible();
    }

    return dispatchOpenFindPreparationsSimilaritySearchDialog({
      substance,
      context,
      originalDatabaseId: databaseId,
    });
  };

  // Breadcrumbs stuff
  _createBreadcrumb = async (params) => {
    try {
      const searchId = await searchStorage.setNew({
        searchType: SearchTypes.MANUAL_SYNTHESIS,
      });
      const { resultSize } = params;
      const crumb = await Breadcrumb.createBreadcrumb({ ...params, searchId }, resultSize);
      Breadcrumb.updateBreadcrumbs(crumb);
      return crumb;
    } catch (error) {
      return Logger.reject(error);
    }
  };

  async _populateToBreadcrumbs(databases, databaseId) {
    const crumbs = [];
    // eslint-disable-next-line no-restricted-syntax
    for (const database of databases) {
      // eslint-disable-next-line no-await-in-loop
      const result = await this._createBreadcrumb(database);
      crumbs.push(result);
    }
    return crumbs.find((crumb) => crumb.databaseId === databaseId) || crumbs[0];
  }

  // Public methods
  // eslint-disable-next-line consistent-return
  async goToSynthesisPreparations(substance, databaseId, context, doNotRunSimilaritySearch = false) {
    try {
      dispatchShowGlobalSpinner();

      const response = await this._findPreparations(substance.details.id, databaseId);
      const { databases } = response;

      dispatchHideGlobalSpinner();

      if (!databases || !databases.length) {
        if (doNotRunSimilaritySearch) {
          showDialogZeroResults();
          return Promise.reject(new Error('Preparations similarity search: no results'));
        }
        // Similarity search
        return this._launchSimilarity(substance, databaseId, context);
      }

      ResultsInfo.clearResultsInfo(); // need to reinitialize database switcher items
      const currentDatabase = await this._populateToBreadcrumbs(databases, databaseId);
      trackFindPreparations({
        state: 'main.results.context',
        substanceId: substance.details.id,
        context,
      });
      Breadcrumb.updateActiveParams(currentDatabase.resultName, currentDatabase.databaseId);
      const pathName = generateResultsPath(currentDatabase);
      history.push({
        pathname: pathName,
        state: currentDatabase,
      });
    } catch (error) {
      dispatchHideGlobalSpinner();
      findPreparationsWSService.unsubscribeAll();
      return Logger.reject(error);
    }
  }

  clearCache() {
    this._cache.removeByKey([serviceCacheKey]);
  }
}

export default new FindPreparationsService();
